import { useSelector } from 'react-redux'
import {
  selectModelList,
  selectSecondaryStandardOptions,
  selectSecondaryStandardValue,
  selectSelectedProductCategory,
} from '../../../../../../features/createCertificate/createCertificateSlice'
import { MultiSelect, MultiSelectChangeParams, MultiSelectProps } from 'primereact/multiselect'
import { sortList } from '../../../../../../utils/helpers/array.helpers'
import { ProductStandardUtils } from '../utils/product-standard-utils'
import { orderBy, unionBy } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import classes from '../AddNewCertProdDetails.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose } from '@fortawesome/free-solid-svg-icons'

export type SecondaryStandardTemplateProps = {
  model: any
  selectedModels: any[]
  isEditMode?: boolean
  isBatchEditMode?: boolean
  slotProps?: {
    multiSelect?: {
      onChange?: (selectedValue: any) => void // isolates state changes
    } & Omit<
      MultiSelectProps,
      'options' | 'optionLabel' | 'optionValue' | 'filter' | 'selectedItemTemplate' | 'onChange'
    >
  }
  modelList?: any[]
}
export default function SecondaryStandardTemplate({
  model,
  selectedModels,
  isEditMode,
  isBatchEditMode,
  slotProps,
  modelList,
}: SecondaryStandardTemplateProps) {
  const allProductStandards = (modelList ?? []).flatMap((item: any) => item.productStandards)
  const currentProductStandards = model.productStandards ?? []
  const secondaryStandardValue = useSelector(selectSecondaryStandardValue)

  const selectedProductStandard = selectedModels
    .flatMap(x => x.productStandards)
    .filter(x => x?.standardLevel === 'Secondary')

  const productType = useSelector(selectSelectedProductCategory)
  let options = (useSelector(selectSecondaryStandardOptions) ?? []).map((item: any) => ({
    ...item,
    ...item.otherProps,
  }))
  options = orderBy(options, ['name'], ['asc'])
  const maxDisplay = 5

  const modelStandard =
    (sortList(
      (model.isForBatchEdit ? selectedProductStandard : currentProductStandards)
        .filter(ProductStandardUtils.filterListBySecondaryStandard)
        .map(ProductStandardUtils.mapStandardByValue),
      'name',
      1
    ) as any[]) ?? []

  let optionsWithValue = unionBy([...modelStandard, ...(options ?? [])], item => item.code)
  optionsWithValue = orderBy(optionsWithValue, ['name'], ['asc'])
  const tooltip = modelStandard.map(item => item.name).join(' \n')
  const topSortedSecondaryStandard = [...modelStandard].slice(0, maxDisplay)

  const initialSelectedValues = model.isForBatchEdit
    ? (secondaryStandardValue ?? []).filter((code: string) =>
        modelStandard.some(item => item.code === code)
      )
    : modelStandard.map(item => item.code)
  const [selectedValues, setSelectedValues] = useState<any[]>([...new Set(initialSelectedValues)])

  const handleRemoveValue = useCallback(
    (event: React.MouseEvent, code: any) => {
      event.stopPropagation()
      const selectedValueWithRemoved = selectedValues.filter(item => item !== code)
      setSelectedValues(selectedValueWithRemoved)
      publishUpdateStateValue(selectedValueWithRemoved)
    },
    [selectedValues]
  )

  const handleOnChange = useCallback((event: MultiSelectChangeParams) => {
    event.stopPropagation()
    setSelectedValues(event.value)
    publishUpdateStateValue(event.value)
  }, [])

  const publishUpdateStateValue = useCallback(
    (value: any[]) => {
      // send update to parent
      if (!slotProps?.multiSelect?.onChange) return

      slotProps.multiSelect.onChange(optionsWithValue.filter(item => value.includes(item.code)))
    },
    [selectedValues, slotProps?.multiSelect, optionsWithValue]
  )

  const placeholder =
    options.length <= 0 ? `None available for ${productType}` : '-- Select Secondary Standard(s) --'

  useEffect(() => {
    if (!isEditMode && !isBatchEditMode) return
    publishUpdateStateValue(selectedValues)
  }, [])

  if (
    (isEditMode || isBatchEditMode) &&
    (selectedModels.map(x => x.assetId).includes(model.assetId) || !model?.assetId)
  )
    return (
      <MultiSelect
        {...{
          ...slotProps?.multiSelect,
          options,
          placeholder,
        }}
        filter={true}
        optionLabel="name"
        optionValue="code"
        value={selectedValues.filter(code =>
          (options ?? []).map((option: any) => option.code).includes(code)
        )}
        onChange={handleOnChange}
        selectedItemTemplate={code => {
          const cacheSelected = optionsWithValue.find(item => item.code === code)

          if (!cacheSelected) {
            return <span className={classes['product-standard__placeholder']}>{placeholder}</span>
          }

          return (
            <div className={classes['product-standard--selected']}>
              <label>{cacheSelected.name}</label>
              {!model.isForBatchEdit && (
                <button
                  onClick={event => handleRemoveValue(event, code)}
                  disabled={slotProps?.multiSelect?.disabled}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              )}
            </div>
          )
        }}
      />
    )

  return (
    <div title={tooltip}>
      {topSortedSecondaryStandard.map(item => (
        <div key={item.code}>{item.name}</div>
      ))}
    </div>
  )
}
